// apps/admin/src/pages/support/components/AvailabilityToggle.tsx
//
// Phase 9.3 Wave 9 Task 18 — Topbar availability toggle (role=support).
// The self-service home for US-5 (P9.3.D-04): a support agent can go
// available / unavailable straight from the admin topbar without opening the
// AgentsTab config surface. Toggling calls `patchMyAvailability`
// (PATCH /agents/me/availability, Task 13); the identity ("me") is resolved
// server-side from the auth token — the client never sends a userId.
//
// Visibility gate (P9.3.D-04, P9.3.D-08): rendered ONLY when the current user
// holds the `support` global role. This is a client-side convenience gate; the
// server re-derives "me" from the token and enforces the self-toggle role tier
// independently (a plain role=support agent is permitted PATCH /me/availability
// but 403s on GET /agents, so we deliberately do NOT pre-fetch the roster —
// there is no GET-self endpoint). The switch therefore defaults to `available`
// and lets the PATCH response drive its state after each toggle.
//
// Optimistic UI (P9.3.D-08): the switch flips immediately on click; on failure
// it rolls back and surfaces the API `user_message` via toast per
// INTERFACE-STANDARDS message priority (error.user_message > message). On
// success the agent-roster caches are invalidated so the AgentsTab (Task 17)
// reflects the new availability, and auto-assign skips the agent when off
// (TS-07, enforced server-side via the roster).
//
// i18n (BẮT BUỘC): every display string (label / aria / tooltip / toasts) goes
// through useTranslation('support') → t(); new keys land in en/vi/fr
// support.json with equal counts. NO hardcoded VN/EN copy.
//
// vg-binding: PLAN:task-18
// vg-binding: P9.3.D-08   — self-availability toggle (PATCH /agents/me/availability)
// vg-binding: P9.3.D-04   — role=support self-service home surface (US-5)
// vg-binding: INTERFACE-STANDARDS:message-priority

import { useState } from 'react';
import type { JSX } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Switch, useToast } from '@printway/ui';

import { patchMyAvailability } from '../../../api/support-agents.api.js';
import { invalidateSupportAgentCaches } from '../../../queries/supportTicketKeys.js';
import { useTranslation } from '../../../i18n/useTranslation.js';
import { useAuthStore } from '../../../stores/useAuthStore.js';
import { ADMIN_GLOBAL_ROLES } from '../../../lib/permissions.js';

/** API error shape surfaced by support-agents.api.ts (SupportTicketApiError). */
interface AgentApiError {
  userMessage?: string;
  code?: string;
}

/** Optimistic-mutation context — the pre-toggle value for rollback. */
interface ToggleContext {
  prev: boolean;
}

/**
 * Availability toggle rendered inside the admin topbar right-slot. Returns
 * `null` for every non-support user so the switch is invisible outside its
 * intended audience.
 */
export function AvailabilityToggle(): JSX.Element | null {
  const { t } = useTranslation('support');
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const user = useAuthStore((s) => s.user);
  const hasRole = useAuthStore((s) => s.hasRole);

  // Local optimistic state. No GET-self endpoint exists (GET /agents is
  // assign-tier; a plain role=support agent → 403), so we default to
  // "available" and let the PATCH response drive the switch after each toggle.
  const [available, setAvailable] = useState(true);

  const mutation = useMutation<
    { available: boolean },
    unknown,
    boolean,
    ToggleContext
  >({
    mutationFn: (next: boolean) => patchMyAvailability(next),
    onMutate: (next: boolean): ToggleContext => {
      const prev = available;
      setAvailable(next); // optimistic flip
      return { prev };
    },
    onError: (err: unknown, _next: boolean, ctx: ToggleContext | undefined) => {
      if (ctx) setAvailable(ctx.prev); // rollback
      const e = err as AgentApiError;
      toast({
        variant: 'error',
        title: e.userMessage ?? t('availability.toggleFailed'),
      });
    },
    onSuccess: (result: { available: boolean }) => {
      setAvailable(result.available); // server truth
      invalidateSupportAgentCaches(queryClient);
      toast({
        variant: 'success',
        title: result.available
          ? t('availability.nowAvailable')
          : t('availability.nowUnavailable'),
      });
    },
  });

  // Client visibility gate — support role only (P9.3.D-04).
  if (!user || !hasRole(ADMIN_GLOBAL_ROLES.SUPPORT)) return null;

  const pending = mutation.isPending;

  return (
    <div
      className="flex items-center gap-2"
      data-testid="topbar-availability"
      title={t('availability.tooltip')}
    >
      <span className="hidden text-xs font-medium text-pw-text-muted sm:inline">
        {available
          ? t('availability.available')
          : t('availability.unavailable')}
      </span>
      <Switch
        checked={available}
        onCheckedChange={(next) => mutation.mutate(next)}
        disabled={pending}
        aria-label={t('availability.toggleAria')}
        data-testid="topbar-availability-toggle"
      />
    </div>
  );
}

export default AvailabilityToggle;
